import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormikWrapper } from "../../../components/formik_wrapper";
import * as actions from "../../../actions";
import { ADMIN, SUPERADMIN } from "../../../constants/roles";
import { Popup } from "../../../components/pop-up";
import { FlatButton, IconButton } from "../../../components/buttons";
import { ACL } from "../../../components/acl";
import { InputToggle, TextBox } from "../../../components/inputs";
import { zuper_pay_countries as countries } from "../../../util/master-data";
import NotFound from "../../common/not_found";
import SearchDropdown from "../../../components/inputs/components/search-dropdown";
import { toast } from "react-toastify";
import { getValueFromNestedObject, setValueToNestedObject } from "../../../util/operations/helper";
import { Badge } from "flowbite-react";

const PATH = {
  POLICY: {
    TAP_TO_PAY: 'zuper_pay.tap_to_pay',
    PAPER_CHEQUE: 'zuper_pay.paper_cheque',
  },
  ZUPER_PAY_ENABLED: 'zuper_pay.is_enabled',
  ZUPER_PAY_EMAIL_PRIMARY: 'account_details.email',
  ZUPER_PAY_EMAIL_SECONDARY: 'account_details.individual.email',
  ZUPER_PAY_COUNTRY: 'account_details.country'
};
const ACCOUNT_NOT_REGISTERED_ERROR = 'No Payment provider found';

export default function ZuperPay({ companyDetails }) {
  const dispatch = useDispatch();
  const [editMode, setEditMode] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  /**
   * @typedef { 'UNAVAILABLE' | 'ACTIVE' | 'INACTIVE' } zuperPayAccountStatus
   */
  const [zuperPayAccountStatus, setZuperPayAccountStatus] = useState('UNAVAILABLE'); 
  const [initialValues, setInitialValues] = useState({
    tapToPay: false,
    emailAddress: '',
    country: '',
    paperCheque: false
  }); // Contains the values from api requests made 

  const zuperPayForm = useFormikWrapper({
    initialValues: initialValues,
    enableReinitialize: true, // Allows Formik to reset when initialValues changes
    onSubmit: () => {
      setOpenModal(true);
    },
  });
  const zuperPayDetails = useSelector((state) => state.company?.zuper_pay);
  const zuperPolicyDetails = useSelector((state) => state.company?.policy);

  // Get the zuper pay details using axios request
  useEffect(() => {
    if (companyDetails.company_uid && zuperPolicyDetails?.zuper_pay?.is_enabled) {
      dispatch(actions.GET_COMPANY_ZUPER_PAY_DETAILS_REQ({ company_uid: companyDetails.company_uid }));
    }
  }, [companyDetails.company_uid, zuperPolicyDetails]);

  useEffect(()=>{
    return () => {
      dispatch(actions.RESET_STATE("company.zuper_pay"));
    };
  }, [])

  // Setting up the form values
  useEffect(() => {
    if (
      zuperPolicyDetails &&
      zuperPayDetails?.type === 'error' && 
      zuperPayDetails?.error?.toString()?.includes(404) && 
      zuperPayDetails?.error?.toString()?.includes(ACCOUNT_NOT_REGISTERED_ERROR)
    ) {
      const fallBack_values = {
        tapToPay: getValueFromNestedObject(zuperPolicyDetails, PATH.POLICY.TAP_TO_PAY),
        emailAddress: '',
        country: '',
        paperCheque: getValueFromNestedObject(zuperPolicyDetails, PATH.POLICY.PAPER_CHEQUE),
      };
      zuperPayForm.setValues(fallBack_values);
      setInitialValues(fallBack_values);
      setZuperPayAccountStatus('INACTIVE');
    } else if (zuperPolicyDetails && zuperPayDetails?.type === 'success') {
      setZuperPayAccountStatus('ACTIVE');
      const _initialValue = {
        tapToPay: getValueFromNestedObject(zuperPolicyDetails, PATH.POLICY.TAP_TO_PAY),
        emailAddress: getValueFromNestedObject(zuperPayDetails, PATH.ZUPER_PAY_EMAIL_PRIMARY) || getValueFromNestedObject(zuperPayDetails, PATH.ZUPER_PAY_EMAIL_SECONDARY),
        country: getValueFromNestedObject(zuperPayDetails, PATH.ZUPER_PAY_COUNTRY),
        paperCheque: getValueFromNestedObject(zuperPolicyDetails, PATH.POLICY.PAPER_CHEQUE)
      };
      setInitialValues(_initialValue);
      zuperPayForm.setValues(_initialValue);
    } else if (zuperPolicyDetails) {
      const fallBack_values = {
        tapToPay: getValueFromNestedObject(zuperPolicyDetails, PATH.POLICY.TAP_TO_PAY),
        paperCheque: getValueFromNestedObject(zuperPolicyDetails, PATH.POLICY.PAPER_CHEQUE)
      };
      zuperPayForm.setValues(fallBack_values);
      setInitialValues(fallBack_values);
    }
  }, [zuperPayDetails, zuperPolicyDetails]);


  async function handleFormSubmit() {
    try {
      const formValue = zuperPayForm.values;
      const isPolicyChanged = formValue?.tapToPay !== initialValues?.tapToPay || formValue?.paperCheque !== initialValues?.paperCheque;
      // If there is any change in policy
      if (isPolicyChanged) {
        const policyForm = JSON.parse(JSON.stringify(zuperPolicyDetails)); // Duplicating the company policy
        setValueToNestedObject(policyForm, PATH.POLICY.TAP_TO_PAY, formValue?.tapToPay, { clone_obj: false });
        setValueToNestedObject(policyForm, PATH.POLICY.PAPER_CHEQUE, formValue?.paperCheque, { clone_obj: false });
        dispatch(
          actions.EDITCOMPANYPOLICY_REQ({
            company_uid: companyDetails.company_uid,
            formData: policyForm,
          })
        );
      }
      // Changes in zuper pay details
      if (((initialValues?.emailAddress !== formValue.emailAddress) || (initialValues.country !== formValue.country)) && zuperPayAccountStatus === 'INACTIVE') {
        dispatch(
          actions.EDIT_ZUPER_PAY_REQ({
            company_uid: companyDetails.company_uid,
            email: formValue.emailAddress,
            country: formValue.country
          })
        );
      }
    } catch (error) {
      console.error(error);
      toast(error?.cause || error.toString());
      zuperPayForm?.resetForm({ values: initialValues });
    }
  }

  return (
    <>
      {zuperPolicyDetails?.zuper_pay?.is_enabled ?
        (<div class="h-full mt-3 rounded-lg">
          <div class="relative h-full w-full bg-white rounded-lg shadow">
            <div class="flow-root">
              <div class={`mx-auto pb-3 border-b sm:flex sm:items-center sm:justify-between ${(editMode && "sticky top-0") || ""
                } py-4 bg-white z-10 sm:px-6 lg:px-8 shadow rounded-t-lg`} 
              >
                <h3 class="text-lg font-semibold leading-6 text-gray-900 flex flex-row gap-2 items-end justify-center">
                  Zuper Pay {
                    zuperPayAccountStatus === 'ACTIVE' ? <Badge size='xs' color='green' className="rounded-full">Active</Badge> :
                    zuperPayAccountStatus === 'INACTIVE' ? <Badge size='xs' color='yellow' className="rounded-full">Not Active</Badge> : 
                    <Badge size='xs' color='red' className="rounded-full">Not Available</Badge>
                  }
                </h3>
                <ACL showFor={[SUPERADMIN, ADMIN]}>
                  <div class="mt-3 sm:ml-4 sm:mt-0">
                    {!editMode && (
                      <div class="flex items-center justify-center space-x-3">
                        <IconButton onClick={() => setEditMode(true)}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            class="w-4 h-4"
                          >
                            <path d="M21.731 2.269a2.625 2.625 0 00-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 000-3.712zM19.513 8.199l-3.712-3.712-12.15 12.15a5.25 5.25 0 00-1.32 2.214l-.8 2.685a.75.75 0 00.933.933l2.685-.8a5.25 5.25 0 002.214-1.32L19.513 8.2z" />
                          </svg>
                        </IconButton>
                      </div>
                    )}
                    {editMode && (
                      <div class="flex items-center justify-center space-x-3">
                        <>
                          <FlatButton
                            appearance="secondary"
                            onClick={async () => {
                              zuperPayForm?.resetForm({ values: initialValues });
                              setEditMode(false);
                            }}
                            className="w-[100px] inline-flex justify-center items-center gap-x-1.5"
                          >
                            Cancel
                          </FlatButton>
                          <FlatButton
                            className="w-[100px] inline-flex justify-center items-center gap-x-1.5"
                            onClick={() => {
                              setOpenModal(true);
                            }}
                          >
                            Save
                          </FlatButton>
                        </>
                      </div>
                    )}
                  </div>
                </ACL>
              </div>
              <div class="inline-block min-w-full py-4 align-middle sm:px-6 lg:px-8">
                <div class="relative mt-0">
                  {!editMode && (
                    <div className="absolute w-full h-full z-30 top-0 left-0"></div>
                  )}
                  <div className={`flex flex-col gap-10 m-4 ${editMode && '!gap-4'}`}>
                    <TextBox
                      formik={zuperPayForm}
                      readOnly={!editMode}
                      disabled={editMode && zuperPayAccountStatus !== 'INACTIVE'}
                      label={'Email Address'}
                      name={'emailAddress'}
                      type='text'
                    />
                    <SearchDropdown
                      formik={zuperPayForm}
                      readOnly={!editMode}
                      disabled={editMode && zuperPayAccountStatus !== 'INACTIVE'}
                      className={``}
                      label={'Country'}
                      name={'country'}
                      type='text'
                      options={countries}
                      optionId="key"
                      optionName="name"
                    />
                    <InputToggle
                      formik={zuperPayForm}
                      readOnly={!editMode}
                      className={``}
                      label={'Tap to Pay'}
                      name={'tapToPay'}
                      type='text'
                    />
                    <InputToggle
                      formik={zuperPayForm}
                      readOnly={!editMode}
                      className={``}
                      label={'Enable Paper Check'}
                      name={'paperCheque'}
                      type='text'
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Popup
            modalState={[openModal, setOpenModal]}
            onConfirm={async () => {
              setOpenModal(false);
              setEditMode(false);
              await handleFormSubmit();
            }}
            content={
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                  />
                </svg>
                <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
                  Are you sure you want to edit the zuper pay of{" "}
                  <b>{companyDetails?.company_name}</b>?
                </h3>
              </>
            }
          />
        </div>) : <NotFound className="rounded-lg mt-3 h-auto"/>
      }
    </>
  );
}